import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Briefcase, User, Store, Loader2, CheckCircle2 } from "lucide-react";
import Logo from "../components/Logo";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import BackButton from "../components/BackButton";
import GoogleLoginButton from "../components/GoogleLoginButton";

const TYPES = [
  { id: "agent", label: "Agente / Rappresentante", desc: "Vendi i nostri prodotti nella tua zona con provvigione", icon: Briefcase },
  { id: "customer", label: "Cliente privato", desc: "Acquista online, storico ordini e indirizzo salvato", icon: User },
  { id: "b2b", label: "Bar / Ristorante / Lido", desc: "Listino operatori, fusti, comodato impianti e agente dedicato", icon: Store },
];
const empty = { account_type: "customer", name: "", email: "", password: "", phone: "", zone: "", business: "", business_type: "", vat: "", address: "", zip: "", city: "", province: "", notes: "" };

export default function Register() {
  const tipo = new URLSearchParams(window.location.search).get("tipo");
  const [form, setForm] = useState({ ...empty, account_type: ["agent", "customer", "b2b"].includes(tipo) ? tipo : "customer" });
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const navigate = useNavigate();
  useEffect(() => { document.title = "Registrati | Guatemalan Spirit"; }, []);
  const f = (k) => ({ value: form[k], onChange: (e) => setForm({ ...form, [k]: e.target.value }), className: "mt-1 h-11" });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) { toast.error("La password deve avere almeno 8 caratteri"); return; }
    setBusy(true);
    try { await api.post("/auth/register", form); setDone(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const t = form.account_type;
  return (
    <div className="min-h-screen paper-grain flex items-center justify-center px-4 py-10 relative">
      <BackButton className="absolute top-4 left-4" />
      <div className="w-full max-w-2xl">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8" data-testid="register-logo"><Logo size={40} /><span className="font-serif text-2xl font-bold text-[#0B2A45]">Guatemalan <span className="text-[#C2593F]">Spirits</span></span></Link>
        <div className="bg-white rounded-2xl border border-stone-200 p-6 sm:p-8">
          {done ? (
            <div className="text-center py-8" data-testid="register-success">
              <CheckCircle2 className="w-14 h-14 text-[#15803D] mx-auto mb-4" />
              <h1 className="font-serif text-2xl font-semibold text-[#0B2A45]">Richiesta ricevuta</h1>
              <p className="text-stone-600 mt-3">Il tuo account è <b>in attesa di approvazione</b>. Riceverai un'email a <b>{form.email}</b> appena l'amministratore lo attiverà.</p>
              <Button onClick={() => navigate("/")} className="mt-6 rounded-full bg-[#0F4C81] text-white h-11 px-8" data-testid="register-home-btn">Torna al sito</Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-5" data-testid="register-form">
              <div>
                <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0B2A45]">Crea il tuo account</h1>
                <p className="text-sm text-stone-500 mt-1">L'accesso viene attivato dall'amministratore dopo una verifica.</p>
              </div>
              <div className="grid sm:grid-cols-3 gap-3" data-testid="register-type-choice">
                {TYPES.map(({ id, label, desc, icon: Icon }) => (
                  <button type="button" key={id} onClick={() => setForm({ ...form, account_type: id })} data-testid={`register-type-${id}`}
                    className={`text-left rounded-xl border p-3 transition-colors ${t === id ? "border-[#0F4C81] bg-[#EFF6FF]" : "border-stone-200 hover:border-stone-300"}`}>
                    <Icon className={`w-5 h-5 mb-2 ${t === id ? "text-[#0F4C81]" : "text-stone-400"}`} />
                    <div className="text-sm font-semibold text-[#0B2A45]">{label}</div>
                    <div className="text-xs text-stone-500 mt-0.5">{desc}</div>
                  </button>
                ))}
              </div>
              {t === "customer" && (
                <div data-testid="register-google-block">
                  <GoogleLoginButton next="/accedi" label="Iscriviti con Google in un tap" />
                  <p className="text-[11px] text-stone-400 text-center mt-2">Accesso immediato con il tuo account Google, senza password.</p>
                  <div className="flex items-center gap-3 mt-4"><span className="flex-1 h-px bg-stone-200" /><span className="text-xs text-stone-400">oppure compila il modulo</span><span className="flex-1 h-px bg-stone-200" /></div>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label>Nome e cognome *</Label><Input required data-testid="register-name" {...f("name")} /></div>
                <div><Label>Telefono</Label><Input type="tel" data-testid="register-phone" {...f("phone")} /></div>
                <div><Label>Email *</Label><Input required type="email" data-testid="register-email" {...f("email")} /></div>
                <div><Label>Password * <span className="text-xs text-stone-400">(min. 8 caratteri)</span></Label><Input required type="password" minLength={8} data-testid="register-password" {...f("password")} /></div>
              </div>
              {t === "agent" && (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="register-agent-fields">
                  <div><Label>Zona di lavoro *</Label><Input required placeholder="Es. Costiera Amalfitana, Cilento" data-testid="register-zone" {...f("zone")} /></div>
                  <div><Label>Esperienza / note</Label><Input placeholder="Es. 5 anni nel beverage HoReCa" {...f("notes")} /></div>
                </div>
              )}
              {t === "b2b" && (
                <div className="grid sm:grid-cols-2 gap-4" data-testid="register-b2b-fields">
                  <div><Label>Nome attività *</Label><Input required data-testid="register-business" {...f("business")} /></div>
                  <div><Label>Tipo attività</Label><Input placeholder="Bar, Lido, Ristorante, Chiosco" {...f("business_type")} /></div>
                  <div><Label>Partita IVA</Label><Input {...f("vat")} /></div>
                  <div><Label>Città *</Label><Input required data-testid="register-city" {...f("city")} /></div>
                  <div className="sm:col-span-2"><Label>Indirizzo</Label><Input {...f("address")} /></div>
                </div>
              )}
              {t === "customer" && (
                <div className="grid sm:grid-cols-6 gap-4" data-testid="register-customer-fields">
                  <div className="sm:col-span-6"><Label>Indirizzo di spedizione</Label><Input {...f("address")} /></div>
                  <div className="sm:col-span-1"><Label>CAP</Label><Input {...f("zip")} /></div>
                  <div className="sm:col-span-4"><Label>Città</Label><Input {...f("city")} /></div>
                  <div className="sm:col-span-1"><Label>Prov.</Label><Input maxLength={2} {...f("province")} /></div>
                </div>
              )}
              {t !== "agent" && <div><Label>Note</Label><Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1" /></div>}
              <Button type="submit" disabled={busy} className="w-full h-12 rounded-full bg-[#0F4C81] hover:bg-[#0A365C] text-white" data-testid="register-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Invia richiesta di registrazione"}
              </Button>
              <p className="text-center text-sm text-stone-500">Hai già un account? <Link to="/accedi" className="text-[#0F4C81] hover:underline" data-testid="register-login-link">Accedi</Link></p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
